import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { fmtDate, fmtMoney } from 'utilities/format';
import { Code, EmptyState, StatusChip, LoadingOverlay } from 'dashboard/components/common';
import InvoiceDialog from './InvoiceDialog';

const STATUS_COLORS = {
  draft: 'default',
  issued: 'info',
  paid: 'success',
  void: 'error',
  refunded: 'warning',
};

/** Sale-invoice list with status filter + per-row state transitions. */
export default function InvoicesTab({ brandId }) {
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listInvoices({ brand_id: brandId, status: statusFilter || undefined })
      .then((res) => setInvoices(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [brandId, statusFilter]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listInvoices({ brand_id: brandId, status: statusFilter || undefined })
      .then((res) => { if (!cancelled) setInvoices(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load', { toastId: 'invoices-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId, statusFilter]);

  const openEdit = async (row) => {
    try {
      const full = await API.getInvoice({ brand_id: brandId, _id: row._id });
      setEditing(full);
      setDlgOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to load invoice');
    }
  };

  const markPaid = (row) => {
    API.markInvoicePaid({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Marked paid'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed', { toastId: 'invoice-mark-paid-error' }));
  };
  const voidIt = (row) => {
    if (!window.confirm('Void this invoice?')) return;
    API.voidInvoice({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Voided'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed', { toastId: 'invoice-void-error' }));
  };
  const remove = (row) => {
    if (!window.confirm('Delete this draft?')) return;
    API.deleteInvoice({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Deleted'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed', { toastId: 'invoice-delete-error' }));
  };

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap='10px' mb='14px'>
        <Box>
          <Typography fontWeight='700'>Invoices</Typography>
          <Typography variant='body2' color='text.secondary'>
            Issue, track and reconcile sale invoices.
          </Typography>
        </Box>
        <Stack direction='row' spacing='8px' flexWrap='wrap'>
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              label='Status'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value=''>All statuses</MenuItem>
              <MenuItem value='draft'>Draft</MenuItem>
              <MenuItem value='issued'>Issued</MenuItem>
              <MenuItem value='paid'>Paid</MenuItem>
              <MenuItem value='void'>Void</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDlgOpen(true); }}
          >
            New invoice
          </Button>
        </Stack>
      </Stack>

      <LoadingOverlay loading={loading && invoices.length === 0} minHeight={200}>
      <TableContainer
        component={Box}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
      >
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Issued</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Total</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && invoices.length === 0 && (
              <TableRow><TableCell colSpan={6}><EmptyState>No invoices yet.</EmptyState></TableCell></TableRow>
            )}
            {invoices.map((inv) => (
              <TableRow key={inv._id}>
                <TableCell><Code>{inv.number || '(draft)'}</Code></TableCell>
                <TableCell>
                  <Typography variant='body2' fontWeight='600'>{inv.customer?.name || '—'}</Typography>
                  <Typography variant='caption' color='text.secondary'>{inv.customer?.email || ''}</Typography>
                </TableCell>
                <TableCell>{fmtDate(inv.issued_at)}</TableCell>
                <TableCell><StatusChip status={inv.status} colors={STATUS_COLORS} /></TableCell>
                <TableCell align='right'>
                  <Typography variant='body2' fontWeight='600'>
                    {fmtMoney(inv.grand_total, inv.currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing='4px' alignItems='center' flexWrap='wrap'>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => openEdit(inv)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    {inv.status === 'issued' && (
                      <Button size='small' color='success' onClick={() => markPaid(inv)}>
                        Mark paid
                      </Button>
                    )}
                    {(inv.status === 'issued' || inv.status === 'paid') && (
                      <Button size='small' variant='outlined' color='error' onClick={() => voidIt(inv)}>
                        Void
                      </Button>
                    )}
                    {inv.status === 'draft' && (
                      <Tooltip title='Delete'>
                        <IconButton size='small' color='error' onClick={() => remove(inv)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </LoadingOverlay>

      <InvoiceDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        brandId={brandId}
        editing={editing}
        onSaved={reload}
      />
    </Box>
  );
}
